import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useState } from "react";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";
import {
  ArrowLeft,
  Wind,
  ChevronLeft,
  ChevronRight,
  Clock,
  Play,
  Sparkles,
  Activity,
  Brain,
  HeartPulse,
  Zap,
  Moon,
  Anchor,
  Target,
  BatteryLow,
  CloudRain,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { FavoriteButton } from "@/components/FavoriteButton";
import {
  CATEGORIES,
  ExerciseGrid,
  ExerciseView,
  type Exercise,
} from "@/lib/toolkit-shared";

export const Route = createFileRoute("/breathing")({
  validateSearch: (search: Record<string, unknown>): { open?: string } => {
    const out: { open?: string } = {};
    if (typeof search.open === "string") out.open = search.open;
    return out;
  },
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Breathing & Regulation | Feeling-Led Breathwork" },
      {
        name: "description",
        content:
          "Choose what feels closest right now and we'll suggest gentle, trauma-informed breathing exercises. No long list, no overwhelm.",
      },
      {
        property: "og:title",
        content: "Breathing & Regulation — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Feeling-led breathing exercises for anxiety, panic, sleep, focus, low mood and grounding.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/breathing" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/breathing" },
    ],
  }),
  component: withMemberGate(BreathingPage, "Breathing"),
});

/* ─────────────── Feelings ─────────────── */

type Feeling = {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** Soft card tint + icon colour, all from brand tokens. */
  tint: string;
  iconTint: string;
  /** Short guidance shown before any exercise. */
  guidanceTitle: string;
  guidance: string;
  blurb: string;
  slugs: string[];
};

const FEELINGS: Feeling[] = [
  {
    id: "anxious",
    label: "Anxious",
    Icon: Activity,
    tint: "border-gold/35 bg-gradient-to-br from-gold-light/50 via-card to-card",
    iconTint: "text-[var(--gold-deep)] bg-gold-light/60",
    guidanceTitle: "Feeling anxious?",
    guidance:
      "Anxiety can make your breathing become quicker and shallower and leave your body feeling on alert. A slower, gentle breathing practice may help your body settle.",
    blurb: "Settle a busy nervous system with steady, supportive breaths.",
    slugs: ["extended-exhale", "box-breathing", "finger-breathing"],
  },
  {
    id: "overwhelmed",
    label: "Overwhelmed",
    Icon: Brain,
    tint: "border-lavender/40 bg-gradient-to-br from-lavender/25 via-card to-card",
    iconTint: "text-[var(--lavender-deep)] bg-lavender/25",
    guidanceTitle: "Feeling overwhelmed?",
    guidance:
      "When there's too much at once, the body can feel loud and crowded. Slow, sensory breathing may help create a little space between you and everything else.",
    blurb: "Soft, sensory practices to make space and slow things down.",
    slugs: ["finger-breathing", "flower-breathing", "feather-breathing"],
  },
  {
    id: "panicky",
    label: "Panicky",
    Icon: HeartPulse,
    tint: "border-blush/50 bg-gradient-to-br from-blush/35 via-card to-card",
    iconTint: "text-[var(--gold-deep)] bg-blush/40",
    guidanceTitle: "Feeling panicky?",
    guidance:
      "Panic often brings a fast heartbeat and quick, shallow breaths. A longer out-breath may help signal to your body that it can begin to slow.",
    blurb: "Quick, one-tap support for moments of panic or sudden overwhelm.",
    slugs: ["panic-reset", "extended-exhale", "heartbeat-breath"],
  },
  {
    id: "stressed",
    label: "Stressed",
    Icon: Zap,
    tint: "border-gold/35 bg-gradient-to-br from-cream/70 via-card to-card",
    iconTint: "text-[var(--gold-deep)] bg-cream",
    guidanceTitle: "Feeling stressed?",
    guidance:
      "Stress can hold tension in the chest, jaw and shoulders. An even, steady rhythm may help release a little of that pressure.",
    blurb: "Steady the body and release some of the pressure.",
    slugs: ["box-breathing", "belly-breathing", "lion-breath"],
  },
  {
    id: "cant-sleep",
    label: "Can't Sleep",
    Icon: Moon,
    tint: "border-lavender/40 bg-gradient-to-br from-lavender/20 via-card to-card",
    iconTint: "text-[var(--lavender-deep)] bg-lavender/25",
    guidanceTitle: "Can't sleep?",
    guidance:
      "A restless body often needs slowing rather than trying harder to sleep. Longer, drowsy out-breaths may invite rest without forcing it.",
    blurb: "Slow, drowsy breaths to invite rest.",
    slugs: ["4-7-8", "koala-breath", "owl-breath"],
  },
  {
    id: "need-grounding",
    label: "Need Grounding",
    Icon: Anchor,
    tint: "border-sage/45 bg-gradient-to-br from-sage-muted/35 via-card to-card",
    iconTint: "text-[var(--sage-deep)] bg-sage-muted/40",
    guidanceTitle: "Need grounding?",
    guidance:
      "Feeling floaty or far away is a common trauma response. Breathing low into the belly may help you feel the weight of your body again.",
    blurb: "Steady, body-anchored breaths to feel here again.",
    slugs: ["belly-breathing", "bear-breath", "forest-breath"],
  },
  {
    id: "need-focus",
    label: "Need Focus",
    Icon: Target,
    tint: "border-gold/35 bg-gradient-to-br from-gold-light/40 via-card to-card",
    iconTint: "text-[var(--gold-deep)] bg-gold-light/60",
    guidanceTitle: "Need focus?",
    guidance:
      "A scattered mind often settles with rhythm. An even, repeating breath pattern may help gather your attention in one place.",
    blurb: "Visual, rhythmic breaths that anchor attention.",
    slugs: ["box-breathing", "star-breathing", "rainbow-breathing"],
  },
  {
    id: "tired",
    label: "Exhausted",
    Icon: BatteryLow,
    tint: "border-sage/40 bg-gradient-to-br from-sage-muted/25 via-card to-card",
    iconTint: "text-[var(--sage-deep)] bg-sage-muted/35",
    guidanceTitle: "Feeling exhausted?",
    guidance:
      "When you're depleted, nothing should feel like effort. These are soft practices that ask very little of you.",
    blurb: "Gentle breaths that don't ask much of you.",
    slugs: ["belly-breathing", "cloud-breath", "dolphin-breath"],
  },
  {
    id: "low-mood",
    label: "Low Mood",
    Icon: CloudRain,
    tint: "border-blush/45 bg-gradient-to-br from-blush/25 via-card to-card",
    iconTint: "text-[var(--gold-deep)] bg-blush/35",
    guidanceTitle: "Low mood today?",
    guidance:
      "Heavy days don't need fixing. A slow, kind breath may offer a small moment of comfort — that's enough.",
    blurb: "Soft, gently uplifting practices for heavy days.",
    slugs: ["dolphin-breath", "rainbow-breathing", "cloud-breath"],
  },
];

/* ─────────────── Helpers ─────────────── */

function patternLines(exercise: Exercise): { label: string; seconds: number }[] {
  const lines: { label: string; seconds: number }[] = [];
  if (exercise.customPhases?.length) {
    for (const p of exercise.customPhases) lines.push({ label: p.name, seconds: p.seconds });
  } else if (exercise.pattern) {
    const { inhale, hold, exhale, holdAfter } = exercise.pattern;
    lines.push({ label: "Inhale", seconds: inhale });
    if (hold) lines.push({ label: "Hold", seconds: hold });
    lines.push({ label: "Exhale", seconds: exhale });
    if (holdAfter) lines.push({ label: "Hold", seconds: holdAfter });
  }
  return lines;
}

function whatYoullDo(exercise: Exercise): string {
  const lines = patternLines(exercise);
  if (lines.length === 0) return "Listen to the guided voice and go at your own pace.";
  return lines.map((l) => `${l.label.toLowerCase()} for ${l.seconds} seconds`).join(", then ");
}

const SAFETY_NOTE =
  "Stop or return to your natural breathing at any time if this feels uncomfortable. Nothing here has to be completed.";

/* ─────────────── Exercise introduction ─────────────── */

function ExerciseIntro({
  exercise,
  onBack,
  onStart,
}: {
  exercise: Exercise;
  onBack: () => void;
  onStart: () => void;
}) {
  const lines = patternLines(exercise);

  return (
    <div className="mx-auto max-w-2xl px-4 pt-6 pb-8 md:px-6 md:pt-10 md:pb-16">
      <button
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <header className="rounded-3xl border border-gold/25 bg-gradient-to-br from-gold-light/40 via-card to-cream/40 px-5 py-7 text-center shadow-[var(--shadow-soft-lg)] md:px-9 md:py-9">
        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--gold-deep)]">
          Before you begin
        </p>
        <h1 className="mt-2.5 font-serif text-[1.6rem] leading-tight tracking-tight text-foreground md:text-4xl">
          {exercise.title}
        </h1>
        {exercise.duration && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card/80 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-foreground/80">
            <Clock className="h-3.5 w-3.5" aria-hidden /> {exercise.duration}
          </div>
        )}
        <div className="mt-4 flex justify-center">
          <FavoriteButton
            storageKey="rt:fav-breathing"
            id={exercise.slug}
            label={exercise.title}
            sub="Breathing exercise"
            to="/breathing"
            variant="pill"
          />
        </div>
      </header>

      <div className="mt-5 space-y-3.5">
        <section className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
            What it is
          </h2>
          <p className="mt-2 text-[15px] leading-[1.7] text-foreground/90">{exercise.blurb}</p>
        </section>

        {exercise.whyItHelps && (
          <section className="rounded-2xl border border-gold/25 bg-cream/50 p-5">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
              How it may help
            </h2>
            <p className="mt-2 whitespace-pre-line text-[15px] leading-[1.75] text-foreground/85">
              {exercise.whyItHelps}
            </p>
          </section>
        )}

        <section className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
            What you'll do
          </h2>
          <p className="mt-2 text-[15px] leading-[1.7] text-foreground/90">
            {whatYoullDo(exercise)}.
          </p>
          {lines.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {lines.map((l, i) => (
                <li
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold-light/30 px-3 py-1 text-xs text-foreground/85"
                >
                  {l.label} · {l.seconds}s
                </li>
              ))}
            </ul>
          )}
          {exercise.rounds && (
            <p className="mt-3 text-sm italic text-muted-foreground">{exercise.rounds}</p>
          )}
        </section>

        <p className="rounded-2xl border border-border/40 bg-cream/40 px-5 py-4 text-[13px] leading-relaxed text-muted-foreground">
          {SAFETY_NOTE}
        </p>
      </div>

      <div className="sticky bottom-24 mt-6 md:static md:bottom-auto">
        <button
          type="button"
          onClick={onStart}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-4 font-serif text-lg text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
        >
          <Play className="h-4 w-4" aria-hidden /> Listen to the guided audio
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Page ─────────────── */

function BreathingPage() {
  const navigate = useNavigate({ from: "/breathing" });
  const search = Route.useSearch();
  const [intro, setIntro] = useState<Exercise | null>(null);
  const [active, setActive] = useState<Exercise | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [feelingId, setFeelingId] = useState<string | null>(null);
  const [browseAll, setBrowseAll] = useState(false);

  // Every step change (feeling → guidance → exercise intro → practice) starts
  // at the top so people always see the title and instructions first.
  useScrollTopOnChange(
    `${feelingId ?? ""}|${intro?.slug ?? ""}|${active?.slug ?? ""}|${browseAll ? "all" : ""}`,
  );

  const breathing = useMemo(() => CATEGORIES.find((c) => c.id === "breathing"), []);
  const allExercises = useMemo<Exercise[]>(() => breathing?.exercises ?? [], [breathing]);
  const bySlug = useMemo(() => {
    const m = new Map<string, Exercise>();
    for (const e of allExercises) m.set(e.slug, e);
    return m;
  }, [allExercises]);

  // Deep-link support: /breathing?open=<slug> opens that exercise's intro.
  useEffect(() => {
    if (!search.open) return;
    const ex = bySlug.get(search.open);
    if (ex) setIntro(ex);
  }, [search.open, bySlug]);

  const feeling = useMemo(
    () => FEELINGS.find((f) => f.id === feelingId) ?? null,
    [feelingId],
  );

  const recommended = useMemo<Exercise[]>(() => {
    if (!feeling) return [];
    return feeling.slugs.map((s) => bySlug.get(s)).filter(Boolean) as Exercise[];
  }, [feeling, bySlug]);

  const searchResults = useMemo<Exercise[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allExercises.filter((e) =>
      [e.title, e.blurb, e.duration, ...(e.tags ?? []), ...(e.bestFor ?? []), e.mantra ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allExercises, searchQuery]);

  const gentleExercises = useMemo(
    () => allExercises.filter((e) => !e.tags?.includes("animal")),
    [allExercises],
  );
  const playfulExercises = useMemo(
    () => allExercises.filter((e) => e.tags?.includes("animal")),
    [allExercises],
  );

  // Guided audio session. Videos are hidden for now.
  if (active) {
    return <ExerciseView exercise={active} onBack={() => setActive(null)} hideVideo />;
  }

  if (intro) {
    return (
      <ExerciseIntro
        exercise={intro}
        onBack={() => setIntro(null)}
        onStart={() => {
          setActive(intro);
          setIntro(null);
        }}
      />
    );
  }

  const showSearch = searchQuery.trim().length > 0;
  const showRecommendations = !!feeling && !showSearch && !browseAll;
  const showBrowseAll = browseAll && !showSearch;
  const showPicker = !feeling && !showSearch && !browseAll;

  return (
    <div
      className="mx-auto max-w-5xl px-4 pt-8 pb-8 md:pb-12"
      data-testid="active-section"
      data-section-id="breathing"
    >

      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Wind className="h-5 w-5 text-foreground" />
        </div>
        <div className="min-w-0">
          <h1 className="font-serif text-2xl text-foreground md:text-4xl">
            Breathing &amp; Regulation
          </h1>
          <p className="mt-1 text-[13px] leading-snug text-muted-foreground md:text-sm">
            Choose what feels closest right now — we'll explain what may help before
            anything begins.
          </p>
        </div>
      </div>

      {(showSearch || showBrowseAll) && (
        <div className="mb-6">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search breathing exercises..."
            className="w-full rounded-full border border-border/60 bg-card px-4 py-2.5 text-sm outline-none focus:border-[var(--gold-deep)]"
            aria-label="Search breathing exercises"
          />
        </div>
      )}

      {/* STEP 1 — feeling picker */}
      {showPicker && (
        <section aria-labelledby="feeling-heading">
          <div className="mb-4 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--gold-deep)]">
              Step 1
            </p>
            <h2
              id="feeling-heading"
              className="mt-1.5 font-display text-xl text-foreground md:text-3xl"
            >
              How are you feeling right now?
            </h2>
            <p className="mx-auto mt-1.5 max-w-md text-[13px] text-muted-foreground">
              Pick whatever feels closest — there's no wrong answer.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-3">
            {FEELINGS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFeelingId(f.id)}
                className={`group flex items-center gap-3 rounded-2xl border px-3 py-3 text-start shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] sm:px-4 sm:py-4 ${f.tint}`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${f.iconTint}`}
                  aria-hidden
                >
                  <f.Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-serif text-[15px] text-foreground">
                    {f.label}
                  </span>
                </span>
                <ChevronRight
                  className="hidden h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 sm:block"
                  aria-hidden
                />
              </button>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setBrowseAll(true)}
              className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-5 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-foreground"
            >
              Browse all exercises
            </button>
          </div>
        </section>
      )}

      {/* STEP 2 — guidance + recommended exercises */}
      {showRecommendations && feeling && (
        <section aria-labelledby="recs-heading">
          <button
            type="button"
            onClick={() => setFeelingId(null)}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Choose a different feeling
          </button>

          <div
            className={`rounded-3xl border p-5 shadow-sm md:p-7 ${feeling.tint}`}
          >
            <span
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${feeling.iconTint}`}
              aria-hidden
            >
              <feeling.Icon className="h-5 w-5" strokeWidth={1.9} />
            </span>
            <h2
              id="recs-heading"
              className="mt-3 font-serif text-xl text-foreground md:text-2xl"
            >
              {feeling.guidanceTitle}
            </h2>
            <p className="mt-2 max-w-prose text-[15px] leading-[1.75] text-foreground/85">
              {feeling.guidance}
            </p>
          </div>

          <div className="mt-6">
            <h3 className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Recommended for you
            </h3>

            {recommended.length > 0 ? (
              <ul className="space-y-2.5">
                {recommended.map((ex) => (
                  <li key={ex.slug}>
                    <button
                      type="button"
                      onClick={() => setIntro(ex)}
                      data-testid="exercise-card"
                      data-slug={ex.slug}
                      className="flex w-full items-center gap-3 rounded-2xl border border-border/50 bg-card px-4 py-3.5 text-start shadow-sm transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)]"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-serif text-[16px] text-foreground">
                          {ex.title}
                        </span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">
                          {ex.blurb}
                        </span>
                        <span className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                          <Clock className="h-3 w-3" aria-hidden /> {ex.duration}
                        </span>
                      </span>
                      <ChevronRight
                        className="h-4 w-4 shrink-0 text-[var(--gold-deep)]"
                        aria-hidden
                      />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-2xl border border-border/40 bg-cream/40 p-6 text-center text-sm text-muted-foreground">
                No matching exercises right now.
              </p>
            )}

            <p className="mt-4 text-center text-[13px] italic leading-relaxed text-muted-foreground">
              {SAFETY_NOTE}
            </p>
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setFeelingId(null);
                setBrowseAll(true);
              }}
              className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-5 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-foreground"
            >
              Browse all exercises
            </button>
          </div>
        </section>
      )}

      {/* Search results override */}
      {showSearch && (
        <section aria-labelledby="search-heading">
          <h2 id="search-heading" className="sr-only">
            Search results
          </h2>
          {searchResults.length > 0 ? (
            <ExerciseGrid exercises={searchResults} onPick={setIntro} isBreathing />
          ) : (
            <p className="rounded-2xl border border-border/40 bg-cream/40 p-6 text-center text-sm text-muted-foreground">
              No breathing exercises matched your search.
            </p>
          )}
        </section>
      )}

      {/* Browse all */}
      {showBrowseAll && (
        <section>
          <button
            type="button"
            onClick={() => setBrowseAll(false)}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Back to feeling picker
          </button>

          {gentleExercises.length > 0 && (
            <section className="mb-12">
              <div className="mb-4">
                <h2 className="font-display text-xl text-foreground md:text-2xl">
                  Gentle breathing
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  For anxiety, sleep, panic, focus and nervous-system regulation.
                </p>
              </div>
              <ExerciseGrid exercises={gentleExercises} onPick={setIntro} isBreathing />
            </section>
          )}

          {playfulExercises.length > 0 && (
            <section className="mb-12">
              <div className="mb-4">
                <h2 className="font-display text-xl text-foreground md:text-2xl">
                  Playful animal breathing
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Gentle breathing inspired by nature and animals.
                </p>
              </div>
              <ExerciseGrid exercises={playfulExercises} onPick={setIntro} isBreathing />
            </section>
          )}
        </section>
      )}

      <div className="mt-10 rounded-2xl border border-border/40 bg-cream/40 p-5 text-center text-sm text-muted-foreground">
        These practices support — they don't replace — therapy or medical care.{" "}
        <Link to="/resources" className="text-primary hover:underline">
          Crisis resources
        </Link>
        .
      </div>
      <div className="mt-8">
        <ComplianceNotice variant="practice" />
      </div>
    </div>
  );
}
